import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { EmployeeValue } from '../../../../models/api/alerts/alert';
import { AlertsService } from '../../../../services/alerts/alerts.service';
import { ButtonComponent } from '../../buttons/button.component';
import { ActionRowComponent } from '../../panels/action-row.component';
import { BaseControlComponent } from '../base-control.component';
import { HelpTextComponent } from '../help-text.component';
import { ClaimantsComponent } from './claimants.component';
import { DialogComponent } from '../../panels/dialog.component';
import { InputComponent } from '../input.component';
import { SsnComponent, SsnType } from '../ssn.component';
import { RadioButtonComponent } from '../radio-button.component';
import { RangeComponent } from '../range.component';

@Component({
  selector: 'app-claimant-selector',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    HelpTextComponent,
    ButtonComponent,
    ActionRowComponent,
    ClaimantsComponent,
    DialogComponent,
    InputComponent,
    SsnComponent,
    RadioButtonComponent,
    RangeComponent,
  ],
  template: `
    <app-action-row>
      <app-button
        label="Add individual"
        left1
        emphasis="Low"
        (click)="add()"
      ></app-button>
    </app-action-row>
    <app-claimants
      [items]="selectedItems"
      (itemsChange)="onItemsChanged($event)"
    />
    <app-dialog
      #dialog
      [visible]="dialogVisible"
      [closable]="false"
      label="Search individuals"
    >
      <ng-template #content>
        <form [formGroup]="fg" class="tw-w-full tw-pt-2">
          <div
            class="tw-flex tw-w-full tw-flex-col tw-gap-2 md:tw-flex-row md:tw-gap-0"
          >
            <app-input
              class="tw-w-full tw-min-w-0 md:tw-w-1/4"
              [control]="fg.controls.firstName"
              label="First Name"
            />
            <app-input
              class="tw-w-full tw-min-w-0 md:tw-w-1/4"
              [control]="fg.controls.lastName"
              label="Last Name"
            />
            <app-input
              class="tw-w-full tw-min-w-0 md:tw-w-1/4"
              [control]="fg.controls.employeeId"
              label="Employee ID"
            />
            <app-ssn
              class="tw-w-full tw-min-w-0 md:tw-w-1/4"
              [control]="fg.controls.ssn"
              label="SSN"
              name="SSN"
              aria-label="SSN"
              [type]="ssnType"
            />
          </div>
          <div
            class="tw-flex tw-w-full tw-flex-col tw-gap-2 md:tw-flex-row md:tw-gap-4"
          >
            <div class="tw-w-full tw-min-w-0 md:tw-basis-1/2">
              <app-range
                label="Date of hire"
                [control]="fg.controls.hireStartDate"
                [toControl]="fg.controls.hireEndDate"
              ></app-range>
            </div>
            <div class="tw-w-full tw-min-w-0 md:tw-basis-1/2">
              <app-radio-button
                label="Source"
                [control]="fg.controls.source"
                optionLabel="name"
                optionValue="source"
                [options]="includeOps"
                [showHelpText]="false"
                [vertical]="false"
              ></app-radio-button>
            </div>
          </div>
        </form>
        <app-action-row useMobileLayout="true">
          <app-button right1 label="Search" (click)="search()" />
          <app-button
            right2
            emphasis="Low"
            label="Clear"
            (click)="clearSearch()"
          />
        </app-action-row>
        <app-claimants
          [items]="items"
          [showDeleteColumn]="false"
          paginatorDropdownAppendTo="self"
          (selectionChanged)="onSelectionChanged($event)"
        />
        <app-action-row useMobileLayout="true">
          <app-button
            [disabled]="!addButtonEnabled"
            right1
            label="Add"
            (click)="addClaimant()"
          />
          <app-button
            right2
            emphasis="Low"
            label="Close"
            (click)="closeDialog()"
          />
        </app-action-row>
      </ng-template>
    </app-dialog>
    @if (showHelpText) {
      <app-help-text [control]="control" [helpText]="helpText" />
    }
  `,
  styles: ``,
})
export class ClaimantSelectorComponent
  extends BaseControlComponent
  implements OnInit
{
  private alerts = inject(AlertsService);

  @Input()
  contractNumber: number;

  ssnType: SsnType = 'LastFour';

  includeOps = [
    { name: 'HR', source: 'hr' },
    { name: 'Claimant', source: 'claimant' },
  ];

  fg = new FormGroup({
    firstName: new FormControl<string>(null),
    lastName: new FormControl<string>(null),
    employeeId: new FormControl<string>(null),
    ssn: new FormControl<string>(null),
    hireStartDate: new FormControl<Date>(null),
    hireEndDate: new FormControl<Date>(null),
    source: new FormControl<string>('hr'),
  });

  items: EmployeeValue[] = [];
  selectedItems: EmployeeValue[] = [];
  selectedSearchItems: EmployeeValue[] = [];
  addButtonEnabled = false;
  dialogVisible: boolean;

  ngOnInit(): void {
    this.ssnType = 'LastFour';

    if (this.control?.value?.length) {
      this.selectedItems = this.control.value.map((item: any) =>
        this.addHireDateValue(item),
      );
    }

    this.control?.valueChanges?.subscribe((value) => {
      if (value?.length && !this.selectedItems?.length) {
        this.selectedItems = value.map((item: any) =>
          this.addHireDateValue(item),
        );
      }
    });
  }

  onSelectionChanged(event: EmployeeValue[]) {
    this.selectedSearchItems = event ?? [];
    this.addButtonEnabled = this.selectedSearchItems.length > 0;
  }

  closeDialog() {
    this.dialogVisible = false;
  }

  addClaimant() {
    const existingIds = new Set(this.selectedItems.map((item) => item.empUnum));
    const newItems = this.selectedSearchItems.filter(
      (item) => !existingIds.has(item.empUnum),
    );
    this.selectedItems = [...this.selectedItems, ...newItems];
    this.control.setValue(this.selectedItems);
    this.dialogVisible = false;
    this.addButtonEnabled = false;
    this.selectedSearchItems = [];
  }

  add() {
    this.dialogVisible = true;
    this.items = [];
    this.selectedSearchItems = [];
    this.ssnType = 'LastFour';
  }

  onItemsChanged(items: EmployeeValue[]) {
    this.selectedItems = items ?? [];
    this.control.setValue(this.selectedItems);
  }

  async search() {
    if (!this.contractNumber) {
      this.items = [];
      return;
    }

    const request = {
      contractNumber: this.contractNumber,
      firstName: this.fg.controls.firstName.value,
      lastName: this.fg.controls.lastName.value,
      employeeId: this.fg.controls.employeeId.value,
      ssn: this.fg.controls.ssn.value,
      useSsnLast4: this.ssnType === 'LastFour',
      hireStartDate: this.formatDate(this.fg.controls.hireStartDate.value),
      hireEndDate: this.formatDate(this.fg.controls.hireEndDate.value),
      source: this.fg.controls.source.value,
    };

    this.items = (await this.alerts.searchIndividuals(request)).map(
      (item: any) =>
        this.addHireDateValue({
          ...item,
          employeeID: item.employeeId ?? null,
          dateHire: item.dateHire ?? item.hireDate ?? null,
          recSource: item.recSource ?? item.source ?? null,
        }),
    );
    this.selectedSearchItems = [];
    this.addButtonEnabled = false;
  }

  clearSearch() {
    this.fg.reset({
      source: 'hr',
    });
    this.items = [];
    this.selectedSearchItems = [];
    this.addButtonEnabled = false;
  }

  private addHireDateValue(item: EmployeeValue): EmployeeValue {
    const parsed = item.dateHire ? new Date(item.dateHire).getTime() : 0;
    return { ...item, hireDateValue: isNaN(parsed) ? 0 : parsed } as any;
  }

  private formatDate(value: Date | null): string | undefined {
    if (!value) {
      return undefined;
    }

    const month = (value.getMonth() + 1).toString().padStart(2, '0');
    const day = value.getDate().toString().padStart(2, '0');
    const year = value.getFullYear();
    return `${month}/${day}/${year}`;
  }
}
